import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { camerasApi } from '@/api/endpoints'
import { queryKeys } from '@/config/queryConfig'
import { useNotifications } from '@/components/notifications/NotificationProvider'
import { useWebSocketContext } from '@/components/WebSocketProvider'
import { formatUtcClock } from '@/lib/clockUtils'

export function CommandOpsStrip() {
  // ── Camera counts ──────────────────────────────────────────────────────────
  // Share the same query key as DashboardPage / PersistentDashboardMap so
  // React Query deduplicates the request — no extra network fetches.
  const { data: camerasPage } = useQuery({
    queryKey: queryKeys.cameras.summary(),
    queryFn: () => camerasApi.getPaginated(1),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  const { data: mapPins = [] } = useQuery({
    queryKey: queryKeys.cameras.mapPins(),
    queryFn: () => camerasApi.getMapPins(),
    staleTime: 60_000,
  })

  const total = camerasPage?.count ?? null
  const online = mapPins.filter(p => p.is_online === true).length

  // ── Unread alerts — same context as Header bell ─────────────────────────
  const { unreadCount } = useNotifications()

  // ── WebSocket state ────────────────────────────────────────────────────────
  const { isConnected } = useWebSocketContext()

  // ── UTC clock — 1s interval, cleanup on unmount ───────────────────────────
  const [clock, setClock] = useState<string>(() => formatUtcClock(new Date()))
  useEffect(() => {
    const id = setInterval(() => setClock(formatUtcClock(new Date())), 1000)
    return () => clearInterval(id)
  }, [])

  const greenDot = online > 0

  return (
    <div className="sticky top-0 z-30 h-8 w-full bg-card/80 backdrop-blur border-b border-border font-mono text-[11px] uppercase tracking-wider flex items-center gap-4 px-3 text-muted-foreground">
      {/* Camera online / total */}
      <span className="flex items-center gap-1.5">
        <span
          className={greenDot ? 'text-[hsl(150,65%,45%)]' : 'text-muted-foreground'}
          aria-hidden="true"
        >
          ●
        </span>
        {total === null ? (
          <span>— / — CAMS ONLINE</span>
        ) : (
          <span>
            <span className={greenDot ? 'text-[hsl(150,65%,45%)]' : undefined}>{online}</span>
            <span>/{total} CAMS ONLINE</span>
          </span>
        )}
      </span>

      {/* Unread alerts */}
      <span>{unreadCount} ALERTS</span>

      {/* WS state */}
      <span className={isConnected ? 'text-[hsl(150,65%,45%)]' : 'text-destructive'}>
        WS {isConnected ? 'LIVE' : 'OFF'}
      </span>

      {/* Spacer pushes clock right */}
      <span className="flex-1" />

      {/* UTC clock */}
      <span>{clock}</span>
    </div>
  )
}
